import { useEffect, useState, useContext } from 'react';
import axios from '../../axios-interceptor';
import { environment } from '../../environments/environment';
import IndividualJobComponent from './IndividualJobComponent';
import CalenderUI from './CalenderUI';
import { Paper, Button } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { StateContext } from '../../contexts/StateContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
const baseURL = environment.baseURL;

export const sortJobs = (jobs, criteria) => {
    const sorted = [...jobs];
    
    switch (criteria) {
        case 'newest':
            return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        case 'oldest':
            return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        case 'createdBy':
            return sorted.sort((a, b) => a.createdBy.localeCompare(b.createdBy));
        default:
            return sorted;
    }
};

export const filterByDateRange = (jobs, from, to) => {
        if (!from || !to) return jobs;
        
        return jobs.filter(job => {
            const jobDate = new Date(job.createdAt);
            return jobDate >= new Date(from) && jobDate <= new Date(to);
        });
    };

const DlmLandingComponent = () => {
    const [completedJobs, setCompletedJobs] = useState([]);
    const [inProgressJobs, setInProgressJobs] = useState([]);
    const [combinedJobs, setCombinedJobs] = useState([]);
    const [sortedData, setSortedData] = useState([]);
    const [sortCriteria, setSortCriteria] = useState('newest'); // 'newest', 'oldest', 'date', 'createdBy'
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setPageHeader } = useContext(StateContext);

    useEffect(() => {
        setPageHeader(t('translation:pageHeader.firmware_update'));
    }, [t, setPageHeader]);

    useEffect(() => {
        if (combinedJobs.length === 0) {
            setSortedData([]);
            return;
        }

        const sorted = combinedJobs.map(section => {
            let filteredJobs = section.jobs;
            
            // Apply date range filter
            if (dateRange.from && dateRange.to) {
                filteredJobs = filterByDateRange(filteredJobs, dateRange.from, dateRange.to);
            }
            
            // Apply sorting
            const sortedJobs = sortJobs(filteredJobs, sortCriteria);
            
            return {
                type: section.type,
                jobs: sortedJobs
            };
        });

        setSortedData(sorted);
    }, [combinedJobs, sortCriteria, dateRange]);


    const fetchCompletedJobs = () => {
        axios
            .get(`${baseURL}/dlm?take=10&state=Completed`)
            .then((res) => {
                setCompletedJobs(res?.data?.items);
            })
            .catch((error) => {
                setCombinedJobs([])
            });

        
    };

    const fetchInProgressJobs = () => {
        axios
            .get(`${baseURL}/dlm?Take=5&State=InProgress`)
            .then((res) => {
                setInProgressJobs(res?.data?.items);
            })
            .catch(() => setInProgressJobs([]))
    };

    useEffect(() => {
        fetchCompletedJobs();
        fetchInProgressJobs();

        // Set up interval to call every 1 minute 
        const intervalId = setInterval(() => {
            fetchCompletedJobs();
            fetchInProgressJobs();
        }, 60000);

        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const fetchAvailableFirmware = async () => {
            try {
                const response = await axios.get(`${baseURL}/assets/firmware/available`);
                return response.data;
            } catch (error) {
                return [];
            }
        };

        const enrichJobsWithDetails = async () => {
            // Add better safety check
            if (!inProgressJobs || !completedJobs || (inProgressJobs?.length === 0 && completedJobs?.length === 0)) {
                return;
            }

            // Fetch available firmware once
            const availableFirmware = await fetchAvailableFirmware();

            // Helper function to enrich a single job
            const enrichJob = async (job) => {
                try {
                   
                    const response = await axios.get(`${baseURL}/dlm/${job.jobId}`);
                    const jobDetails = response.data;

                    // Find matching firmware from available list
                    const firmwareMatch = availableFirmware.find(
                        (firmware) => firmware.id === jobDetails.firmwareImageId
                    );

                    // Calculate percentage from firmwareUpdateStatus
                    const totalCount = jobDetails.totalDevices;
                    const processedCount = jobDetails.firmwareUpdateStatus.reduce(
                        (sum, status) => {
                            if (status.status === 'Succeeded' || status.status === 'Failed') {
                                return sum + status.count;
                            }
                            return sum;
                        },
                        0
                    );
                    const percentage = totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0;

                    // Build deviceToVersionInfo
                    const deviceType = firmwareMatch?.deviceType || jobDetails.deviceType || '';
                    const version = firmwareMatch?.version || '';
                    const deviceToVersionInfo = version ? `${deviceType} → v${version}` : deviceType;

                    const siteIds = jobDetails.siteJobs?.map(s => s.siteId) ?? [];
                    const uniqueSiteCount = new Set(siteIds).size;

                    return {
                        createdAt: jobDetails.createdAt,
                        createdBy: jobDetails.createdBy,
                        deviceToVersionInfo: deviceToVersionInfo,
                        siteCountInfo: `${uniqueSiteCount} ${uniqueSiteCount === 1 ? 'site' : 'sites'}`,
                        deviceCountInfo: `${jobDetails.totalDevices} devices`,
                        totalDevices: jobDetails.totalDevices,
                        percentage: percentage,
                        status: jobDetails.status,
                        firmwareUpdateStatus: jobDetails.firmwareUpdateStatus || [],
                    };
                } catch (error) {
                    // Return basic job structure on error
                    const siteIds = job.siteJobs?.map(s => s.siteId) ?? [];
                    const uniqueSiteCount = new Set(siteIds).size;

                    return {
                        createdAt: job.createdAt,
                        createdBy: job.createdBy,
                        deviceToVersionInfo: job.deviceType || '',
                        siteCountInfo: `${uniqueSiteCount} ${uniqueSiteCount === 1 ? 'site' : 'sites'}`,
                        deviceCountInfo: `${job.totalDevices} devices`,
                        totalDevices: job.totalDevices,
                        percentage: 0,
                        status: job.status,
                        firmwareUpdateStatus: job.firmwareUpdateStatus || [],
                    };
                }
            };

            // Enrich all jobs in parallel with safety checks
            const enrichedInProgressJobs =
                inProgressJobs && inProgressJobs?.length > 0
                    ? await Promise.all(inProgressJobs?.map((job) => enrichJob(job)))
                    : [];

            const enrichedCompletedJobs =
                completedJobs && completedJobs?.length > 0
                    ? await Promise.all(completedJobs?.map((job) => enrichJob(job)))
                    : [];

            // Set combined jobs
            setCombinedJobs([
                { type: 'InProgressJobs', jobs: enrichedInProgressJobs },
                { type: 'CompletedJobs', jobs: enrichedCompletedJobs },
            ]);
        };

        enrichJobsWithDetails();
    }, [inProgressJobs, completedJobs]);


   return (
        <div className="dlm-landing-component">
            <div className="dlm-landing-page-holder">
                <div className="section-filter">
                    <CalenderUI 
                        onDateRangeChange={setDateRange}
                        onSortChange={setSortCriteria}
                    />
                    <Button
                        className="start-new-job-button"
                        variant="contained"
                        color="primary"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => navigate('/dlm/new-job')}
                    >
                        {t('translation:pages.dlm.landing_page.start_new_job_button')}
                    </Button>
                </div>
                <div className="section-inprogress">
                    <div className="in-progress-title">{t('translation:pages.dlm.landing_page.inprogress_title')}</div>

                    <Paper elevation={3} className="paper-inprogress">
                        {(() => {
                            const inProgressSection = sortedData.find((item) => item.type === 'InProgressJobs');
                            return !inProgressSection?.jobs || inProgressSection.jobs.length === 0 ? (
                                <div className="no-jobs-inprogress">
                                    {t('translation:pages.dlm.landing_page.nojobs_inprogress')}
                                </div>
                            ) : (
                                inProgressSection.jobs.map((job, index) => (
                                    <IndividualJobComponent key={index} job={job} calledFor="InProgress" />
                                ))
                            );
                        })()}
                    </Paper>
                </div>
                <div className="section-completed">
                    <div className="completed-title">{t('translation:pages.dlm.landing_page.completed_title')}</div>
                    <Paper elevation={3} className="paper-completed">
                        {(() => {
                            const completedSection = sortedData.find((item) => item.type === 'CompletedJobs');
                            return !completedSection?.jobs || completedSection.jobs.length === 0 ? (
                                <div className="no-jobs-completed">
                                    {t('translation:pages.dlm.landing_page.nojobs_completed')}
                                </div>
                            ) : (
                                completedSection.jobs.map((job, index) => (
                                    <IndividualJobComponent key={index} job={job} calledFor="Completed" />
                                ))
                            );
                        })()}
                    </Paper>
                </div>
            </div>
        </div>
    );
};


export default DlmLandingComponent;
